import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getCountFromServer, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useVisitor } from '../../context/VisitorContext';
import { getVisitorDiscoveries, setDiscoveryFavorite, grantXp } from '../../services/discoveryService';
import { getCompletedMissionIds, markMissionCompleted } from '../../services/missionsService';
import { computeMissions } from '../../utils/missions';
import { levelForXp, tierForLevel, xpProgressInLevel } from '../../utils/levels';
import './game.css';

const UNKNOWN_ARTIST_KEY = '__sin_artista__';

export default function Collection() {
  const { visitorId, profile, refreshProfile } = useVisitor();
  const [allPlacements, setAllPlacements] = useState([]);
  const [discoveries, setDiscoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | discovered | locked | favorites
  const [artistFilter, setArtistFilter] = useState(null);
  const [tab, setTab] = useState('collection'); // collection | missions
  const [completedMissionIds, setCompletedMissionIds] = useState(null);
  const [achievementToast, setAchievementToast] = useState(null);
  const [fragmentsByModel, setFragmentsByModel] = useState({});
  const grantingRef = useRef(false);
  const fragmentsFetchedRef = useRef(new Set());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const scenesSnap = await getDocs(query(collection(db, 'geoScenes'), where('isPublic', '==', true)));
        const scenes = scenesSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

        const placementsBySene = await Promise.all(scenes.map(async (scene) => {
          const snap = await getDocs(collection(db, 'geoScenes', scene.id, 'placements'));
          return snap.docs.map((d) => ({
            id: d.id,
            sceneId: scene.id,
            sceneName: scene.name || 'Escena',
            ...(d.data() || {})
          }));
        }));

        setAllPlacements(placementsBySene.flat());
      } catch (e) {
        console.error('Error loading collection universe:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!visitorId) return;
    getVisitorDiscoveries(visitorId).then(setDiscoveries).catch((e) => console.error(e));
    getCompletedMissionIds(visitorId).then(setCompletedMissionIds).catch((e) => console.error(e));
  }, [visitorId]);

  const discoveryById = useMemo(() => {
    const map = new Map();
    discoveries.forEach((d) => map.set(d.id, d));
    return map;
  }, [discoveries]);

  const items = useMemo(() => {
    return allPlacements.map((p) => {
      const discovery = discoveryById.get(p.id);
      return {
        ...p,
        isDiscovered: !!discovery,
        favorite: !!discovery?.favorite,
        restored: !!discovery?.restored
      };
    });
  }, [allPlacements, discoveryById]);

  const missions = useMemo(() => computeMissions({ items, discoveries }), [items, discoveries]);

  useEffect(() => {
    const modelIds = Array.from(new Set(
      items.filter((i) => i.isDiscovered && i.modelId).map((i) => i.modelId)
    )).filter((id) => !fragmentsFetchedRef.current.has(id));
    if (modelIds.length === 0) return;
    modelIds.forEach((id) => fragmentsFetchedRef.current.add(id));
    (async () => {
      const entries = await Promise.all(modelIds.map(async (modelId) => {
        try {
          const snap = await getCountFromServer(collection(db, 'models', modelId, 'anchors'));
          return [modelId, snap.data().count];
        } catch (e) {
          console.error('Error contando fragmentos:', e);
          return [modelId, 0];
        }
      }));
      setFragmentsByModel((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
  }, [items]);

  useEffect(() => {
    if (!visitorId || completedMissionIds == null || loading || grantingRef.current) return;
    const toGrant = missions.filter((m) => m.completed && !completedMissionIds.has(m.id));
    if (toGrant.length === 0) return;

    grantingRef.current = true;
    (async () => {
      let lastToast = null;
      for (const mission of toGrant) {
        try {
          await markMissionCompleted(visitorId, mission.id);
          await grantXp(visitorId, mission.xpBonus);
          lastToast = mission;
        } catch (e) {
          console.error('Error completando misión:', e);
        }
      }
      setCompletedMissionIds((prev) => new Set([...(prev || []), ...toGrant.map((m) => m.id)]));
      await refreshProfile();
      if (lastToast) setAchievementToast(lastToast);
      grantingRef.current = false;
    })();
  }, [missions, completedMissionIds, visitorId, loading, refreshProfile]);

  const level = profile ? levelForXp(profile.xp || 0) : 1;
  const tier = tierForLevel(level);
  const xpProgress = xpProgressInLevel(profile?.xp || 0);

  const artistIndex = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = (it.artist || '').trim() || UNKNOWN_ARTIST_KEY;
      if (!map.has(key)) map.set(key, { key, name: key === UNKNOWN_ARTIST_KEY ? 'Sin artista' : key, count: 0 });
      map.get(key).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => (a.key === UNKNOWN_ARTIST_KEY ? 1 : b.count - a.count));
  }, [items]);

  const visibleItems = useMemo(() => {
    return items.filter((it) => {
      if (filter === 'discovered' && !it.isDiscovered) return false;
      if (filter === 'locked' && it.isDiscovered) return false;
      if (filter === 'favorites' && !it.favorite) return false;
      if (artistFilter) {
        const key = (it.artist || '').trim() || UNKNOWN_ARTIST_KEY;
        if (key !== artistFilter) return false;
      }
      return true;
    });
  }, [items, filter, artistFilter]);

  const stats = useMemo(() => {
    const total = items.length;
    const discovered = items.filter((it) => it.isDiscovered).length;
    const restored = items.filter((it) => it.restored).length;
    const favorites = items.filter((it) => it.favorite).length;
    const artistsDiscovered = new Set(
      items.filter((it) => it.isDiscovered && it.artist).map((it) => it.artist)
    ).size;
    return { total, discovered, restored, favorites, artistsDiscovered };
  }, [items]);

  const toggleFavorite = async (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!visitorId || !item.isDiscovered) return;
    const nextFavorite = !item.favorite;
    setDiscoveries((prev) => prev.map((d) => (d.id === item.id ? { ...d, favorite: nextFavorite } : d)));
    try {
      await setDiscoveryFavorite(visitorId, item.id, nextFavorite);
    } catch (err) {
      console.error('Error updating favorite:', err);
    }
  };

  return (
    <div className="collection-wrap">
      <div className="collection-header">
        <span className="geo-eyebrow-badge">Museo Digital · Museo Virtual</span>
        <h1>Tu colección{profile?.name ? `, ${profile.name}` : ''}</h1>
        <p>Descubrí esculturas recorriendo el museo y armá tu colección personal.</p>
      </div>

      <div className="collection-level-card">
        <div className="collection-level-badge">{level}</div>
        <div className="collection-level-info">
          <strong>Nivel {level} · {tier.name}</strong>
          <div className="collection-level-bar">
            <div className="collection-level-bar-fill" style={{ width: `${xpProgress.percent}%` }} />
          </div>
          <span className="geo-muted">{xpProgress.current}/{xpProgress.total} XP para el próximo nivel</span>
        </div>
      </div>

      <div className="collection-stats">
        <div className="collection-stat-card">
          <span className="collection-stat-value">{stats.discovered}/{stats.total}</span>
          <span className="collection-stat-label">Descubiertas</span>
        </div>
        <div className="collection-stat-card">
          <span className="collection-stat-value">{stats.restored}/{stats.total}</span>
          <span className="collection-stat-label">Restauradas</span>
        </div>
        <div className="collection-stat-card">
          <span className="collection-stat-value">{stats.artistsDiscovered}</span>
          <span className="collection-stat-label">Artistas descubiertos</span>
        </div>
        <div className="collection-stat-card">
          <span className="collection-stat-value">{stats.favorites}</span>
          <span className="collection-stat-label">Favoritas</span>
        </div>
      </div>

      <div className="collection-tabs">
        <button className={`collection-tab ${tab === 'collection' ? 'active' : ''}`} onClick={() => setTab('collection')}>
          <i className="bi bi-grid-3x3-gap-fill" /> Colección
        </button>
        <button className={`collection-tab ${tab === 'missions' ? 'active' : ''}`} onClick={() => setTab('missions')}>
          <i className="bi bi-flag-fill" /> Misiones
          {missions.filter((m) => m.completed).length > 0 && (
            <span className="collection-tab-count">{missions.filter((m) => m.completed).length}/{missions.length}</span>
          )}
        </button>
      </div>

      {tab === 'collection' ? (
        <>
          <div className="collection-filters">
            {[
              { key: 'all', label: 'Todas' },
              { key: 'discovered', label: 'Descubiertas' },
              { key: 'locked', label: 'Por descubrir' },
              { key: 'favorites', label: 'Favoritas' }
            ].map((f) => (
              <button
                key={f.key}
                className={`collection-filter-chip ${filter === f.key ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
            {artistIndex.length > 1 && artistIndex.map((a) => (
              <button
                key={a.key}
                className={`collection-filter-chip ${artistFilter === a.key ? 'active' : ''}`}
                onClick={() => setArtistFilter((prev) => (prev === a.key ? null : a.key))}
              >
                {a.name} ({a.count})
              </button>
            ))}
          </div>

          {loading ? (
            <p className="geo-muted">Cargando colección...</p>
          ) : visibleItems.length === 0 ? (
            <div className="collection-empty">
              <p>No hay obras que coincidan con este filtro todavía.</p>
            </div>
          ) : (
            <div className="collection-grid">
              {visibleItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.isDiscovered ? `/geoscenes/${item.sceneId}` : '#'}
                  className={`collection-card ${item.isDiscovered ? '' : 'is-locked'}`}
                  onClick={(e) => { if (!item.isDiscovered) e.preventDefault(); }}
                >
                  <div className="collection-card-media">
                    {item.isDiscovered && (
                      <button
                        className={`collection-favorite-btn ${item.favorite ? 'active' : ''}`}
                        onClick={(e) => toggleFavorite(item, e)}
                        title={item.favorite ? 'Quitar de favoritas' : 'Marcar como favorita'}
                      >
                        <i className={`bi ${item.favorite ? 'bi-heart-fill' : 'bi-heart'}`} />
                      </button>
                    )}
                    {item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt={item.isDiscovered ? (item.title || item.modelName) : 'Obra por descubrir'} />
                    ) : (
                      <i className="bi bi-gem collection-card-lock-icon" />
                    )}
                    {!item.isDiscovered && !item.thumbnailUrl && (
                      <i className="bi bi-lock-fill collection-card-lock-icon" />
                    )}
                    {item.isDiscovered && fragmentsByModel[item.modelId] > 0 && (
                      <span className="collection-fragments-badge" title="Fragmentos culturales">
                        <i className="bi bi-collection-fill" /> {fragmentsByModel[item.modelId]}
                      </span>
                    )}
                    {item.restored && (
                      <span className="collection-restored-badge" title="Escultura restaurada">
                        <i className="bi bi-patch-check-fill" />
                      </span>
                    )}
                  </div>
                  <div className="collection-card-body">
                    <p className="collection-card-name">
                      {item.isDiscovered ? (item.title || item.modelName || 'Escultura') : '???'}
                    </p>
                    <p className="collection-card-meta">
                      {item.isDiscovered ? (item.artist || item.sceneName) : 'Por descubrir'}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="missions-list">
          {missions.map((m) => (
            <div key={m.id} className={`mission-card ${m.completed ? 'is-complete' : ''}`}>
              <div className="mission-icon">
                <i className={`bi ${m.icon}`} />
              </div>
              <div className="mission-body">
                <div className="mission-title-row">
                  <strong>{m.title}</strong>
                  <span className="mission-xp">+{m.xpBonus} XP</span>
                </div>
                <p>{m.description}</p>
                <div className="mission-progress-bar">
                  <div className="mission-progress-fill" style={{ width: `${Math.min(100, Math.round((m.progress / Math.max(m.total, 1)) * 100))}%` }} />
                </div>
                <span className="geo-muted">{m.progress}/{m.total}</span>
              </div>
              {m.completed && <i className="bi bi-check-circle-fill mission-check" />}
            </div>
          ))}
        </div>
      )}

      {achievementToast && (
        <div className="achievement-toast" onClick={() => setAchievementToast(null)}>
          <i className="bi bi-trophy-fill" />
          <div>
            <strong>¡Misión cumplida! {achievementToast.title}</strong>
            <span>+{achievementToast.xpBonus} XP</span>
          </div>
        </div>
      )}
    </div>
  );
}
